/**
 * Support — the customer's own ticket queue, and the form that opens one.
 *
 * Two states and no more, matching the backend: open or closed. There is no
 * "pending", no "awaiting customer", no priority field. Who wrote the last message is
 * already visible in the thread, and every extra dimension here is one more thing to
 * keep accurate on the customer's screen for no benefit to them.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { useAccount, useCreateTicket, useTickets } from '@/shared/api/hooks'
import type { PreparedAttachment } from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import { AttachmentPicker } from '@/shared/ui/AttachmentPicker'
import {
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Fig,
  Loading,
  Notice,
  Panel,
  formatDateTime,
  formatRelative,
} from '@/shared/ui/primitives'

/** Mirrors support.MaxTitleLength / MaxBodyLength on the server. Duplicated so the
 *  form can count down rather than submit into a 400. */
const MAX_TITLE = 140
const MAX_BODY = 8000
const MAX_IMAGES = 5

export function TicketStatusBadge({ status }: { status: 'open' | 'closed' }) {
  return status === 'open' ? (
    <Badge tone="signal" dot>
      open
    </Badge>
  ) : (
    <Badge>closed</Badge>
  )
}

export function Support() {
  const [cursor, setCursor] = useState('')
  const { data, isPending, error } = useTickets(cursor)

  return (
    <AppShell>
      <PageHead
        index="05"
        eyebrow="Support"
        title="Support"
        subtitle="Open a ticket and follow it here. Screenshots are compressed in your browser before they are uploaded, so a 4 MB capture costs a few hundred kilobytes."
        actions={
          <Link to="/support/new">
            <Button variant="primary">New ticket</Button>
          </Link>
        }
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        {isPending && <Loading label="Loading your tickets" />}
        <ErrorNotice error={error} />

        <Panel title="Your tickets" index="01" padded={false}>
          {data && data.tickets.length === 0 && (
            <Empty>
              No tickets yet. If something is wrong — or merely strange —{' '}
              <Link to="/support/new" className="signal">
                open one
              </Link>
              .
            </Empty>
          )}

          {data && data.tickets.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Status</th>
                    <th>Replies</th>
                    <th>Images</th>
                    <th>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>
                        <Link to={`/support/${ticket.id}`} className="signal">
                          {ticket.title}
                        </Link>
                        <div className="mono-note">{ticket.id}</div>
                      </td>
                      <td>
                        <TicketStatusBadge status={ticket.status} />
                      </td>
                      <td className="num">
                        <Fig>{ticket.comment_count}</Fig>
                      </td>
                      <td className="num">
                        <Fig>{ticket.attachment_count}</Fig>
                      </td>
                      <td className="mono-note" title={formatDateTime(ticket.last_activity_at)}>
                        {formatRelative(ticket.last_activity_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {data?.next_cursor && (
          <div className="row" style={{ justifyContent: 'center' }}>
            <Button onClick={() => setCursor(data.next_cursor ?? '')}>Load older tickets</Button>
          </div>
        )}
        {cursor && (
          <div className="row" style={{ justifyContent: 'center' }}>
            <Button variant="ghost" onClick={() => setCursor('')}>
              Back to the newest
            </Button>
          </div>
        )}
      </div>
    </AppShell>
  )
}

export function NewTicket() {
  const navigate = useNavigate()
  const { data: account } = useAccount()
  const create = useCreateTicket()

  // `chi` links a crash here with the title and body already written (#918), the way it
  // used to prefill a GitHub issue. The person still reads and submits it themselves.
  const [params] = useSearchParams()
  const [title, setTitle] = useState(() => params.get('title') ?? '')
  const [body, setBody] = useState(() => params.get('body') ?? '')
  const [attachments, setAttachments] = useState<PreparedAttachment[]>([])

  const attachmentsEnabled = account?.support_attachments_enabled ?? false
  // 2 MiB, matching the server's default cap. The compressor aims below it; this is
  // the number it aims at.
  const maxBytes = 2 * 1024 * 1024

  // Previews are revoked by the picker on unmount, but a successful submit navigates
  // away with the list still populated, so the keys are released here too.
  useEffect(() => {
    return () => {
      for (const attachment of attachments) URL.revokeObjectURL(attachment.previewUrl)
    }
    // Intentionally empty: this is an unmount-only cleanup over the latest list, and
    // depending on `attachments` would revoke a preview on every add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const titleOk = title.trim().length >= 3
  const bodyOk = body.trim().length >= 1

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!titleOk || !bodyOk) return
    create.mutate(
      { title: title.trim(), body, attachments },
      { onSuccess: (ticket) => navigate(`/support/${ticket.id}`) },
    )
  }

  return (
    <AppShell>
      <PageHead
        index="05"
        eyebrow="Support"
        title="Open a ticket"
        subtitle="What you expected, what happened, and — if you have one — a screenshot. A repository size and a language help more than an apology for the length."
        actions={
          <Link to="/support">
            <Button variant="ghost">Cancel</Button>
          </Link>
        }
      />

      <form onSubmit={onSubmit}>
        <Panel
          title="New ticket"
          index="01"
          footer={
            <div className="row row--between">
              <span className="mono-note">
                We answer in the thread, and email you when there is a reply.
              </span>
              <Button
                type="submit"
                variant="primary"
                loading={create.isPending}
                disabled={!titleOk || !bodyOk}
              >
                Open ticket
              </Button>
            </div>
          }
        >
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <Field
              label="Title"
              hint={`One line. ${MAX_TITLE - title.length} characters left.`}
            >
              <input
                className="input"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={MAX_TITLE}
                minLength={3}
                required
                autoFocus
                placeholder="chi index . fails on a monorepo with submodules"
              />
            </Field>

            <Field
              label="What is happening"
              hint={`Paste output as-is — whitespace is kept exactly as you type it. ${(MAX_BODY - body.length).toLocaleString()} characters left.`}
            >
              <textarea
                className="input"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                maxLength={MAX_BODY}
                required
                rows={12}
                style={{ resize: 'vertical', fontFamily: 'var(--mono)', fontSize: '0.875rem' }}
                placeholder={
                  'What I ran:\n  chi index .\n\nWhat I expected:\n\nWhat happened instead:\n\nRepository: ~40k files, mostly TypeScript'
                }
              />
            </Field>

            {attachmentsEnabled ? (
              <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                <span className="field__label">Screenshots</span>
                <AttachmentPicker
                  attachments={attachments}
                  onChange={setAttachments}
                  max={MAX_IMAGES}
                  maxBytes={maxBytes}
                  disabled={create.isPending}
                />
              </div>
            ) : (
              <Notice>
                Image attachments are not available on this deployment. Paste the text of any
                error instead — it is more searchable than a screenshot anyway.
              </Notice>
            )}

            <ErrorNotice error={create.error} />
          </div>
        </Panel>
      </form>
    </AppShell>
  )
}
